import React, { useEffect, useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import Modal, { IModalChildProps } from '../../../common/modal';
import {
    IDeleteLine,
    IItemImage,
    IOrderDetails,
} from '../../../../models/order';
import './modal-styles.css';
import { RootState } from '../../../../store';
import { connect } from 'react-redux';
import {
    loadOrder,
    deleteLineItem,
} from '../../../../store/order/order-actions';
import { setIsBusy } from '../../../../store/api/api-status-action';
import { IITAPISResult } from '../../../../models/result-models';
import { gaEvent } from '../../../../hooks/ga-hook';

interface IDeleteModal extends IModalChildProps {
    order: IOrderDetails;
    deleteLineItem: (lineToDelete: IDeleteLine) => Promise<void>;
    loadOrder: (orderNumber: string) => Promise<void>;
    setIsBusy: (isBusy: boolean) => Promise<void>;
    lineToDelete: IDeleteLine;
    deleteLineItemResponse: IITAPISResult | null;
    itemImage: IItemImage | undefined;
}

const DeleteModal = ({
    itemImage,
    lineToDelete,
    onAccept,
    onClose,
    order,
    deleteLineItem,
    loadOrder,
    setIsBusy,
    deleteLineItemResponse,
}: IDeleteModal) => {
    const intl = useIntl();
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    useEffect(() => {
        if (!deleteLineItemResponse) return;

        if (deleteLineItemResponse!.returnCodeType === 'Error') {
            setError(true);
        } else {
            setSuccess(true);
        }
    }, [deleteLineItemResponse]);
    useEffect(() => {
        setSuccess(false);
        setError(false);
    }, []);
    const onDelete = () => {
        gaEvent(
            'Estore_Order_Updates',
            'Delete_Modal',
            order.billTo.accountNumber
        );
        setIsBusy(true);
        deleteLineItem(lineToDelete).then(() => {
            setIsBusy(false);
        });
    };

    return (
        <Modal>
            <>
                <div className="modal-header">
                    <button
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label={intl.formatMessage({
                            id: 'Close',
                        })}
                        onClick={onClose}
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                {itemImage && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <img
                            src={itemImage.src}
                            alt={intl.formatMessage({
                                id: itemImage.alt,
                                defaultMessage: itemImage.alt,
                            })}
                            style={{
                                width: '100px',
                                height: 'auto',
                            }}
                        />
                    </div>
                )}
                {!success && !error && (
                    <p className="text-center modal-spacing">
                        <FormattedMessage
                            id="Are_you_sure_delete"
                            defaultMessage="Are you sure you want to delete this item?"
                        />
                    </p>
                )}
                {!success && error && (
                    <p className="text-center modal-spacing">
                        <FormattedMessage
                            id="delete_items_error_message"
                            defaultMessage="We're sorry there's been an error deleting this item.
                        Please try again."
                        />
                    </p>
                )}
                {success && !error && (
                    <p className="text-center">
                        <span className="fa fa-check icon-green"></span>
                        <strong>
                            <FormattedMessage
                                id="Item_successfully_deleted"
                                defaultMessage="Item successfully deleted"
                            />
                        </strong>
                    </p>
                )}
                <div className="modal-footer">
                    {!success && !error && (
                        <>
                            <button
                                type="button"
                                className="btn btn-default"
                                onClick={onClose}
                            >
                                <FormattedMessage
                                    id="Cancel"
                                    defaultMessage="Cancel"
                                />
                            </button>
                            <button className="btn btn-info" onClick={onDelete}>
                                <FormattedMessage
                                    id="Confirm"
                                    defaultMessage="Confirm"
                                />
                            </button>
                        </>
                    )}
                    {!success && error && (
                        <>
                            <button
                                name="delete-cancel"
                                type="button"
                                className="btn btn-default"
                                onClick={onClose}
                            >
                                <FormattedMessage
                                    id="Cancel"
                                    defaultMessage="Cancel"
                                />
                            </button>
                            <button
                                name="delete-retry-button"
                                type="button"
                                className="btn btn-default"
                                onClick={() => {
                                    setError(false);
                                }}
                            >
                                <FormattedMessage
                                    id="Retry"
                                    defaultMessage="Retry"
                                />
                            </button>
                        </>
                    )}
                    {success && !error && (
                        <button
                            name="delete-close"
                            type="button"
                            className="btn btn-default"
                            onClick={onAccept}
                        >
                            <FormattedMessage
                                id="Close"
                                defaultMessage="Close"
                            />
                        </button>
                    )}
                </div>
            </>
        </Modal>
    );
};
const mapStateToProps = (state: RootState) => {
    return {
        deleteLineItemResponse: state.deleteLineItemResponse
            ? state.deleteLineItemResponse.data!
            : null,
    };
};

const mapDispatchToProps = {
    deleteLineItem,
    loadOrder,
    setIsBusy,
};

const DeleteModalContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(DeleteModal);

export default DeleteModalContainer;
